import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, filter, map, Observable} from 'rxjs';
import {Project} from 'src/app/core/model/project';
import {SkillModel} from 'src/app/core/model/skill';
import {User} from 'src/app/core/model/user';
import {selectSkillsList} from 'src/app/core/store/skills/skills.selector';
import {selectAllTeams} from 'src/app/core/store/teams/teams.selector';
import {selectAllUsers} from 'src/app/core/store/users/users.selector';
import {Team} from 'src/app/teams/shared/model/team';
import {PipesModule} from '../../pipes/pipes.module';
import {ProjectTaskDialogPanels} from '../projects-task-dialog/project-task-dialog-constants';
import {TaskDialogRequirementItemComponent} from './item/task-dialog-requirement-item.component';
import {OphButtonGroupComponent, OphButtonGroupOption} from '../../design/oph-button-group/oph-button-group.component';
import {OphBackButtonComponent} from '../../design/oph-back-button/oph-back-button.component';

@Component({
  selector: 'task-dialog-requirements',
  standalone: true,
  imports: [
    CommonModule,
    TaskDialogRequirementItemComponent,
    PipesModule,
    OphButtonGroupComponent,
    OphBackButtonComponent,
  ],
  templateUrl: './task-dialog-requirements.component.html',
  styleUrls: ['./task-dialog-requirements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDialogRequirementsComponent implements OnInit {
  @HostBinding('class.oph-scroll-bar') readonly ophScrollBar = true;

  @Input() form: FormGroup;
  @Input() project: Project;

  @Output() back = new EventEmitter();
  @Output() addPeople = new EventEmitter();

  requirements$: Observable<SkillModel[]> = this.store$.pipe(select(selectSkillsList));
  teams$: Observable<Team[]> = this.store$.pipe(select(selectAllTeams));
  users$: Observable<User[]> = this.store$.pipe(select(selectAllUsers));
  filteredUsers$: Observable<User[]>;
  filteredTeams$: Observable<Team[]>;
  filteredTeamUsers$: Observable<User[]>;
  combinedUsers$: Observable<User[]>; // This is all users from individual and team

  selectedRequirements$ = new BehaviorSubject<Record<string, Record<string, boolean>>>({});

  ProjectTaskDialogPanels = ProjectTaskDialogPanels;

  readonly options: OphButtonGroupOption[] = [
    {name: 'people', display: 'People'},
    {name: 'skills', display: 'Skills'},
  ];

  selectedOption: 'people' | 'teams' | 'skills' = 'people';

  constructor(private store$: Store) {}

  ngOnInit() {
    this.initializeFilteredObservables();
  }

  private initializeFilteredObservables(): void {
    this.filteredUsers$ = this.users$.pipe(
      filter(users => !!users),
      map(users => users.filter(user => this.project.users.includes(user._id)))
    );

    this.filteredTeams$ = this.teams$.pipe(
      filter(teams => !!teams),
      map(teams => teams.filter(team => this.project.teams.includes(team.id)))
    );

    this.filteredTeamUsers$ = this.filteredTeams$.pipe(
      filter(teams => !!teams),
      map(teams => teams.flatMap(team => team.users || []))
    );

    this.combinedUsers$ = combineLatest([this.filteredTeamUsers$, this.filteredUsers$]).pipe(
      filter(([users, filteredUsers]) => !!users && !!filteredUsers),
      map(([users, filteredUsers]) => {
        const userMap = new Map<string, User>();
        [...users, ...filteredUsers].forEach(user => userMap.set(user._id, user));
        return Array.from(userMap.values());
      })
    );
  }

  onOption(checked: boolean, id: string, type: string) {
    let control: AbstractControl;
    if (type === 'teams') {
      control = this.teamsControl;
    } else if (type === 'users') {
      control = this.usersControl;
    } else {
      control = this.skillsControl;
    }

    if (!checked) {
      control.patchValue([...control.value].filter(v => v !== id));
    } else {
      control.patchValue([...control.value, id]);
    }
  }

  onAddPeople(): void {
    this.addPeople.emit();
  }

  get usersControl(): AbstractControl {
    return this.form.get('users');
  }

  get teamsControl(): AbstractControl {
    return this.form.get('teams');
  }

  get skillsControl(): AbstractControl {
    return this.form.get('skills');
  }
}
